/**
 * Text tool font picker: grouped catalog (custom @font-face + Google Fonts).
 * Render routing: see fontFamilyRegistry.ts (which families use CDN vs local files).
 */
import {
  GOOGLE_TEXT_FONT_FAMILIES,
  isGoogleTextFontFamily,
  resolveFontFamilyForCanvas,
} from './fontFamilyRegistry';

export { GOOGLE_TEXT_FONT_FAMILIES, isGoogleTextFontFamily, resolveFontFamilyForCanvas };

export type TextFontOption = string | { label: string; value: string };

export type TextFontGroup = {
  id: string;
  label: string;
  fonts: TextFontOption[];
};

export const normalizeFontOption = (
  font: TextFontOption,
): { label: string; value: string } => {
  if (typeof font === 'string') {
    return { label: font, value: font };
  }
  return font;
};

export const TEXT_FONT_GROUPS: TextFontGroup[] = [
  {
    id: 'sans',
    label: 'Sans serif',
    fonts: [
      'Poppins',
      { label: 'Inter', value: 'Inter' },
      { label: 'Montserrat', value: 'Montserrat' },
      { label: 'Roboto', value: 'Roboto' },
      { label: 'Open Sans', value: 'Open Sans' },
      { label: 'Raleway', value: 'Raleway' },
      { label: 'Nunito', value: 'Nunito' },
      { label: 'Rubik', value: 'Rubik' },
      { label: 'Work Sans', value: 'Work Sans' },
      { label: 'Figtree', value: 'Figtree' },
      { label: 'Lexend', value: 'Lexend' },
      { label: 'Josefin Sans', value: 'Josefin Sans' },
      { label: 'Space Grotesk', value: 'Space Grotesk' },
      { label: 'Titillium Web', value: 'Titillium Web' },
      { label: 'DM Sans', value: 'DM Sans' },
      { label: 'Manrope', value: 'Manrope' },
      { label: 'Outfit', value: 'Outfit' },
      { label: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans' },
      { label: 'Urbanist', value: 'Urbanist' },
      { label: 'Antonio', value: 'Antonio' },
      { label: 'A Bee Zee', value: 'ABeeZee' },
      { label: 'Philosopher', value: 'Philosopher' },
    ],
  },
  {
    id: 'serif',
    label: 'Serif',
    fonts: [
      { label: 'Playfair Display', value: 'Playfair Display' },
      { label: 'Cormorant Garamond', value: 'Cormorant Garamond' },
      { label: 'Cinzel', value: 'Cinzel' },
      { label: 'Merriweather', value: 'Merriweather' },
      { label: 'Fraunces', value: 'Fraunces' },
      { label: 'Bodoni Moda', value: 'Bodoni Moda' },
      { label: 'Lora', value: 'Lora' },
      { label: 'DM Serif Display', value: 'DM Serif Display' },
      { label: 'Libre Baskerville', value: 'LibreBaskerville' },
      { label: 'Berkshire Swash', value: 'BerkshireSwash' },
      { label: 'Georgia', value: 'Georgia' },
      { label: 'Times New Roman', value: 'TimesNewRoman' },
      { label: 'Claredon', value: 'Claredon' },
    ],
  },
  {
    id: 'script',
    label: 'Script',
    fonts: [
      { label: 'Great Vibes', value: 'GreatVibes' },
      { label: 'Allura', value: 'Allura' },
      { label: 'Italianno', value: 'Italianno' },
      { label: 'Alex Brush', value: 'AlexBrush' },
      { label: 'Alexis Marie', value: 'AlexisMarie' },
      { label: 'Dancing Script', value: 'Dancing Script' },
      { label: 'Parisienne', value: 'Parisienne' },
      { label: 'Pinyon Script', value: 'Pinyon Script' },
      { label: 'Tangerine', value: 'Tangerine' },
      { label: 'Satisfy', value: 'Satisfy' },
      { label: 'Carattere', value: 'Carattere' },
      { label: 'Meie Script', value: 'Meie Script' },
      { label: 'Pacifico', value: 'Pacifico' },
      { label: 'Caveat', value: 'Caveat' },
      { label: 'Yellowtail', value: 'Yellowtail' },
      { label: 'Petit Formal Script', value: 'PetitFormalScript' },
      { label: 'Better Times', value: 'BetterTimes' },
      { label: 'Enchanting', value: 'Enchanting' },
      { label: 'Bakery', value: 'bakery' },
      { label: 'Abuget', value: 'Abuget' },
      { label: 'Fontspring', value: 'Fontspring' },
      { label: 'Lhandw', value: 'Lhandw' },
      { label: 'Wayfarer', value: 'Wayfarer' },
    ],
  },
  {
    id: 'display',
    label: 'Display',
    fonts: [
      { label: 'Anton', value: 'Anton' },
      { label: 'Bebas Neue', value: 'Bebas Neue' },
      { label: 'Staatliches', value: 'Staatliches' },
      { label: 'Teko', value: 'Teko' },
      { label: 'Righteous', value: 'Righteous' },
      { label: 'Paytone One', value: 'Paytone One' },
      { label: 'Russo One', value: 'Russo One' },
      { label: 'Bungee', value: 'Bungee' },
      { label: 'Fredoka', value: 'Fredoka' },
      { label: 'Shrikhand', value: 'Shrikhand' },
      { label: 'League Spartan', value: 'League Spartan' },
      { label: 'Abril Fatface', value: 'Abril Fatface' },
      { label: 'Oswald', value: 'Oswald' },
      { label: 'Lilita One', value: 'Lilita One' },
      { label: 'Archivo Black', value: 'Archivo Black' },
      { label: 'Amaranth', value: 'Amaranth' },
      { label: 'Blackjack', value: 'Blackjack' },
      { label: 'Domaine', value: 'Domaine' },
      { label: 'Gothic', value: 'Gothic' },
      { label: 'Manta', value: 'Manta' },
      { label: 'Mvboli', value: 'Mvboli' },
      { label: 'Thunder', value: 'Thunder' },
      { label: 'Argo Flats', value: 'ArgoFlats' },
    ],
  },
  {
    id: 'condensed',
    label: 'Condensed',
    fonts: [
      { label: 'Roboto Condensed', value: 'Roboto Condensed' },
      { label: 'Barlow Condensed', value: 'Barlow Condensed' },
      { label: 'Archivo Narrow', value: 'Archivo Narrow' },
      { label: 'Saira Condensed', value: 'Saira Condensed' },
      { label: 'Fira Sans Condensed', value: 'FiraSansCondensed' },
    ],
  },
];

export const TEXT_FONTS: TextFontOption[] = TEXT_FONT_GROUPS.flatMap(
  (group) => group.fonts,
);

const fontLabelByValue = new Map(
  TEXT_FONTS.map((font) => {
    const { label, value } = normalizeFontOption(font);
    return [value, label] as const;
  }),
);

export const getFontLabel = (value: string): string =>
  fontLabelByValue.get(value) ?? value;

export const GOOGLE_TEXT_FONTS_LINK_ID_PREFIX = 'respark-google-text-fonts';

/** Keep each stylesheet URL under ~2 KB (proxy/CDN limits). */
const GOOGLE_FONTS_FAMILIES_PER_CHUNK = 18;

const buildGoogleFontsCssUrl = (families: string[]): string =>
  `https://fonts.googleapis.com/css2?${families
    .map((family) => `family=${family.replace(/ /g, '+')}:wght@400;700`)
    .join('&')}&display=swap`;

export const GOOGLE_TEXT_FONTS_CSS_URLS: string[] = (() => {
  const urls: string[] = [];
  for (let i = 0; i < GOOGLE_TEXT_FONT_FAMILIES.length; i += GOOGLE_FONTS_FAMILIES_PER_CHUNK) {
    urls.push(
      buildGoogleFontsCssUrl(
        GOOGLE_TEXT_FONT_FAMILIES.slice(i, i + GOOGLE_FONTS_FAMILIES_PER_CHUNK),
      ),
    );
  }
  return urls;
})();

/** @deprecated First chunk only — prefer batched `GOOGLE_TEXT_FONTS_CSS_URLS`. */
export const GOOGLE_TEXT_FONTS_CSS_URL = GOOGLE_TEXT_FONTS_CSS_URLS[0] ?? '';

/** Inject Google stylesheets once (used by text tool + template font preload). */
export function ensureGoogleTextFontsLoaded(): void {
  if (typeof document === 'undefined') {
    return;
  }
  GOOGLE_TEXT_FONTS_CSS_URLS.forEach((href, index) => {
    const id = `${GOOGLE_TEXT_FONTS_LINK_ID_PREFIX}-${index}`;
    if (document.getElementById(id)) {
      return;
    }
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  });
}
